export const FEATURES = [
  {
    slug: "digital-admissions",
    category: "Smart Enrollment Hub",
    categorySlug: "admissions",
    iconName: "GraduationCap",
    heroImage: "/school/admission.webp",
    title: "Admission Management",
    tagline: "From online application to enrolled student without a single paper form.",
    heroStat: [
      { value: "100%", label: "Online applications" },
      { value: "Auto", label: "Document checklist" },
      { value: "1-click", label: "Convert to student" },
    ],
    problemStatement: "Admission season is the most chaotic time of the school year piles of forms, missing documents, frantic calls, and manual data entry into registers. It doesn't have to be.",
    capabilities: [
      {
        icon: "FileText",
        title: "Application Form Management",
        desc: "Build customisable online and offline admission forms with custom fields, document requirements, and class-specific questions. Publish on your website with one click.",
      },
      {
        icon: "Kanban",
        title: "Admission Funnel Tracking",
        desc: "Drag applications through stages applied, shortlisted, confirmed. Full team visibility across the entire admissions pipeline.",
      },
      {
        icon: "FolderUp",
        title: "Document Upload & Verification",
        desc: "Applicants upload required documents online. System auto-checks completeness and flags missing items for staff review.",
      },
      {
        icon: "CalendarPlus",
        title: "Entrance Test / Interview Scheduling",
        desc: "Schedule entrance tests and interviews directly from the pipeline. Automated confirmation emails and reminders go to parents.",
      },
      {
        icon: "UsersRound",
        title: "Seat Allocation & Waitlist Management",
        desc: "Allocate seats per class and manage waitlists automatically. Notify waitlisted applicants when seats open up.",
      },
      {
        icon: "ChartColumnBig",
        title: "Admission Reports & Analytics",
        desc: "Real-time view of application volume, conversion rates, and stage-wise drop-offs. Accepted applicants convert to student records in one click no re-entry.",
      },
    ],
    whoBenefits: [
      { role: "Admissions team", benefit: "Manage hundreds of applicants without losing track of anyone every application visible in one pipeline." },
      { role: "Front desk staff", benefit: "No more paper form sorting or manual data entry applications arrive digitally and already organised." },
      { role: "School principal", benefit: "Real-time view of application volume and conversion at any point during admissions season." },
      { role: "Parents (applicants)", benefit: "Apply from home, track their application status online, and never wonder whether documents were received." },
    ],
    howItWorks: [
      { step: "01", title: "Parent applies online", desc: "Fills the form and uploads documents from any device. Receives an automatic acknowledgement." },
      { step: "02", title: "Team reviews and progresses", desc: "Admissions team moves the application through stages, schedules interviews, and issues offers all from one screen." },
      { step: "03", title: "Convert to enrolled student", desc: "One click converts the accepted application to a full student profile. Admission done." },
    ],
    mockupType: "admissions",
    relatedSlugs: ["student-information-system", "fee-finance", "front-office", "id-cards-certificates"],
  },

  {
    slug: "student-information-system",
    category: "Central Student Hub",
    categorySlug: "sis",
    title: "Student Information System",
     iconName: "Users",
    heroImage: "/school/studentprofile.webp",
    tagline: "Every student's complete story from first admission to final result in one place.",
    heroStat: [
      { value: "100%", label: "Paperless records" },
      { value: "< 5s", label: "Student lookup" },
      { value: "∞", label: "History retained" },
    ],
    problemStatement: "Most schools juggle paper registers, Excel sheets, and scattered folders for student data. Updating one record means touching five different places and something always falls through.",
    capabilities: [
      {
        icon: "BadgeInfo",
        title: "Student Profiles",
        desc: "One profile per student covering personal, academic, and medical information enrolment details, class history, guardian contacts, emergency numbers, and medical notes.",
      },
      {
        icon: "LayoutGrid",
        title: "Class & Section Allocation",
        desc: "Assign and reassign students to classes and sections effortlessly. Bulk promotion at year-end with a single action.",
      },
      {
        icon: "CheckCheck",
        title: "Attendance Tracking",
        desc: "Daily and monthly attendance analytics per student. Teachers mark in under 2 minutes; parents notified instantly on absence.",
      },
      {
        icon: "ShieldAlert",
        title: "Behavior & Discipline Records",
        desc: "Log incidents, warnings, and commendations against student profiles. Track patterns and share with parents through the portal.",
      },
      {
        icon: "ArrowUpCircle",
        title: "Promotion & Transfer Records",
        desc: "Manage year-end promotions, lateral transfers, and TC issuance all linked to the student's cumulative academic history.",
      },
      {
        icon: "Search",
        title: "Instant Smart Search",
        desc: "Find any student in under 5 seconds by name, roll number, guardian phone, or admission number. Bulk filters by class, section, board, or custom tags.",
      },
    ],
    whoBenefits: [
      { role: "Admissions team", benefit: "Onboard new students in minutes with guided data entry and auto-generated roll numbers." },
      { role: "Class teachers", benefit: "Access their section roster instantly with parent contacts and medical alerts at a glance." },
      { role: "School admin", benefit: "Run board compliance reports and data audits without chasing down separate registers." },
      { role: "Parents", benefit: "View their child's profile, documents, and academic history through the parent portal." },
    ],
    howItWorks: [
      { step: "01", title: "Admit the student", desc: "Fill the digital admission form data flows directly into the student profile, no re-entry." },
      { step: "02", title: "Upload documents once", desc: "Attach required documents to the profile. Set reminders for pending documents." },
      { step: "03", title: "Profile stays updated automatically", desc: "Attendance, fees, results, and promotions update the profile in real time throughout the year." },
    ],
    mockupType: "student-profile",
    relatedSlugs: ["digital-admissions", "parent-portal", "health-medical", "cumulative-records"],
  },

  {
    slug: "academic-calendar",
    category: "Schedule & Planner",
    categorySlug: "academic",
    title: "Academic Planning",
    heroImage: "/school/academic.webp",
    iconName: "CalendarClock" ,
    tagline: "Every holiday, exam, event, and period planned, published, and always in sync.",
    heroStat: [
      { value: "1 place", label: "All school events" },
      { value: "Auto", label: "Timetable conflicts" },
      { value: "Instant", label: "Substitution alerts" },
    ],
    problemStatement: "Academic planning lives in the principal's head, a wall calendar, and five different spreadsheets. When something changes, nobody finds out in time. There's a better way.",
    capabilities: [
      {
        icon: "CalendarRange",
        title: "Academic Calendar",
        desc: "Publish the full school calendar holidays, exam periods, events, and PTMs visible to staff, students, and parents in real time.",
      },
      {
        icon: "Timer",
        title: "Timetable Management",
        desc: "Build and publish teacher and student timetables. Detect clashes automatically and resolve them before the term starts.",
      },
      {
        icon: "CalendarClock",
        title: "Period Scheduling & Substitution",
        desc: "Schedule periods per class and subject. When a teacher is absent, assign a substitute and notify the replacement instantly.",
      },
      {
        icon: "PartyPopper",
        title: "Event Scheduling",
        desc: "Plan school events sports day, annual day, parent meetings with venue booking, participant lists, and automated reminders.",
      },
      {
        icon: "Bell",
        title: "Notifications & Reminders",
        desc: "Automated reminders go to the right people teachers get period reminders, parents get event notices, staff get exam duty alerts.",
      },
    ],
    whoBenefits: [
      { role: "Academic coordinator", benefit: "Plan the full year in one place and keep timetables conflict-free without manual checking." },
      { role: "Teachers", benefit: "See their personal timetable, substitution duties, and upcoming events from their dashboard." },
      { role: "Parents", benefit: "Know about school events, exams, and holidays well in advance no last-minute surprises." },
      { role: "School admin", benefit: "Coordinate events and substitutions without making dozens of phone calls." },
    ],
    howItWorks: [
      { step: "01", title: "Set up the academic year", desc: "Define terms, holidays, exam windows, and key events at the start of the year." },
      { step: "02", title: "Build and publish timetables", desc: "Assign periods to teachers and classes. Conflicts flagged automatically resolve before publishing." },
      { step: "03", title: "Manage changes in real time", desc: "Update events, substitutions, or exam dates stakeholders notified instantly." },
    ],
    mockupType: "calendar",
    relatedSlugs: ["student-information-system", "examination-results", "hrms", "co-curricular"],
  },

  {
    slug: "fee-finance",
    category: "Revenue & Collections",
    categorySlug: "finance",
    title: "Fee & Finance Management",
    heroImage: "/school/finance.webp",
    iconName: "Wallet",
    tagline: "95%+ collection rate. Zero manual reconciliation. Parents pay in 30 seconds.",
    heroStat: [
      { value: "95%+", label: "Avg collection rate" },
      { value: "UPI / NEFT / Card", label: "Payment modes" },
      { value: "Auto", label: "GST receipts" },
    ],
    problemStatement: "Fee collection is the lifeblood of every school and yet most schools still track it in spreadsheets, issue hand-written receipts, and spend days every month reconciling who paid what.",
    capabilities: [
      {
        icon: "Sliders",
        title: "Fee Structure Setup",
        desc: "Configure class-wise and category-wise fee structures term, monthly, or custom instalment plans. Apply concessions, scholarships, and sibling discounts with rules.",
      },
      {
        icon: "CreditCard",
        title: "Online Payment Gateway Integration",
        desc: "Accept fees via UPI, NEFT, IMPS, debit/credit card, and cash. PCI-DSS compliant no card data touches your servers.",
      },
      {
        icon: "Receipt",
        title: "Fee Collection & Receipts",
        desc: "Every payment generates a GST-compliant receipt instantly emailed to parents and stored against the student profile.",
      },
      {
        icon: "AlertTriangle",
        title: "Pending Dues Tracking",
        desc: "Schedule SMS and app reminders for upcoming due dates, overdue fees, and instalment deadlines with escalating urgency.",
      },
      {
        icon: "PercentCircle",
        title: "Discounts, Fines & Scholarships",
        desc: "Apply and track fee discounts, late payment fines, and scholarship deductions per student all reflected automatically in ledger entries.",
      },
      {
        icon: "LineChart",
        title: "Finance Insights & Reporting",
        desc: "Visualize real-time financial data with dynamic charts, automated reports, and actionable insights for smarter revenue and fee management.",
      },
    ],
    whoBenefits: [
      { role: "Accounts team", benefit: "Eliminate manual reconciliation every payment matches automatically to the correct student and fee head." },
      { role: "School principal", benefit: "See live collection rates and outstanding dues from the dashboard no need to ask the accounts team." },
      { role: "Parents", benefit: "Pay from their phone in 30 seconds, get an instant receipt, and see their full payment history any time." },
      { role: "Management", benefit: "Accurate, real-time financial data for budgeting and board reporting no end-of-month scramble." },
    ],
    howItWorks: [
      { step: "01", title: "Configure fee structures", desc: "Set up fee heads, amounts, due dates, and concession rules once at the start of the year." },
      { step: "02", title: "Parents receive and pay", desc: "Fee demands are generated automatically. Parents pay online; reminders go out automatically for overdue accounts." },
      { step: "03", title: "Auto-reconcile and report", desc: "Every payment reconciles in real time. Month-end reports generate themselves." },
    ],
    mockupType: "fee-collection",
    relatedSlugs: ["accounts-finance", "parent-portal", "scholarship-management", "transport"],
  },

  {
    slug: "examination-results",
    category: "Assessment & Grading",
    categorySlug: "examination",
    title: "Examination & Results Management",
    heroImage: "/school/examresult.webp",
    iconName: "ClipboardList" ,
    tagline: "Build exams. Auto-grade. Generate report cards. All without a third-party tool.",
    heroStat: [
      { value: "Auto", label: "Grading & results" },
      { value: "Question bank", label: "Reusable across years" },
      { value: "Real-time", label: "Performance analytics" },
    ],
    problemStatement: "Conducting exams means printing papers, manual checking, entering marks into another system, calculating grades by hand, and then typing report cards. It takes weeks. It doesn't have to.",
    capabilities: [
      {
        icon: "FlaskConical",
        title: "Exam Creation",
        desc: "Create unit tests, semester exams, and final examinations. Schedule timed online exams with randomised questions, or manage offline exams with mark entry.",
      },
      {
        icon: "PenLine",
        title: "Mark Entry",
        desc: "Enter marks manually per student or import in bulk from an Excel template. MCQ exams grade automatically the moment they're submitted.",
      },
      {
        icon: "Zap",
        title: "Automated Result Generation",
        desc: "Results compute instantly after mark entry total marks, percentage, subject-wise scores, and pass/fail status all auto-calculated.",
      },
      {
        icon: "Medal",
        title: "Grade & GPA Calculation",
        desc: "Configure custom grading scales and GPA systems per board or class. Grades assign automatically based on your rules.",
      },
      {
        icon: "ScrollText",
        title: "Report Card Generation",
        desc: "Generate board-formatted report cards as print-ready PDFs in bulk or deliver digitally to parent emails.",
      },
      {
        icon: "ChartNoAxesColumn",
        title: "Performance Analytics",
        desc: "Class-level and student-level analytics identify weak subjects, top performers, and students needing intervention.",
      },
    ],
    whoBenefits: [
      { role: "Subject teachers", benefit: "Build a question bank once and reuse it across batches and years save hours every exam season." },
      { role: "Students", benefit: "Take online exams from school or home. Get results and feedback immediately after submission." },
      { role: "Academic coordinator", benefit: "Schedule multiple exams across classes, monitor progress, and generate class-wise analysis reports." },
      { role: "Parents", benefit: "Receive result notifications and see subject-wise performance breakdowns from the parent app." },
    ],
    howItWorks: [
      { step: "01", title: "Create and schedule the exam", desc: "Set exam type, subjects, duration, and eligible students. Online exams publish automatically; offline exams await mark entry." },
      { step: "02", title: "Enter or auto-collect marks", desc: "Import marks in bulk or let the system auto-grade online exams. Review and approve before publishing." },
      { step: "03", title: "Generate results and report cards", desc: "Results publish instantly. Report cards generate in bulk print or deliver digitally." },
    ],
    mockupType: "mcq-exam",
    relatedSlugs: ["lms", "student-information-system", "id-cards-certificates", "competitive-exams"],
  },

  {
    slug: "hrms",
    category: "Staff & Workforce",
    categorySlug: "hr",
    heroImage: "/school/hrms.webp",
    iconName: "BadgeCheck",
    title: "Human Resource Management",
    tagline: "Complete employee records from recruitment to exit all in one place.",
    heroStat: [
      { value: "100%", label: "Digital HR records" },
      { value: "Zero", label: "Paper files" },
      { value: "Custom", label: "Roles & policies" },
    ],
    problemStatement: "HR files in physical folders, qualification records in one spreadsheet, emergency contacts in another. When you need any employee information, you go hunting. That ends here.",
    capabilities: [
      {
        icon: "UserCog",
        title: "Employee Profiles",
        desc: "Name, designation, department, qualifications, certifications, employment dates, salary grade, and emergency contacts one complete profile per employee.",
      },
      {
        icon: "CheckSquare",
        title: "Attendance & Leave Management",
        desc: "Digital leave applications, one-click approvals, live leave balance tracking, and automatic payroll integration for LOP and encashments.",
      },
      {
        icon: "UserPlus",
        title: "Recruitment & Onboarding",
        desc: "Manage job postings, applicant tracking, interview scheduling, and digital onboarding forms new staff set up in minutes.",
      },
      {
        icon: "Star",
        title: "Performance Evaluation",
        desc: "Configure appraisal cycles, set KPIs, collect manager feedback, and maintain performance history for every employee.",
      },
      {
        icon: "FolderOpen",
        title: "Staff Documents & Contracts",
        desc: "Store joining letters, contracts, PAN, Aadhaar, degree certificates, and appraisal records all uploaded, searchable, and role-access controlled.",
      },
      {
        icon: "",
        title: "Exit Management",
        desc: "Manage resignations, calculate dues, generate experience letters, and process full-and-final settlements all from the same profile.",
      },
    ],
    whoBenefits: [
      { role: "HR administrator", benefit: "Onboard new staff in minutes with guided data entry no physical forms to collect and file." },
      { role: "School principal", benefit: "See the complete staff roster, qualifications, and subject assignments at a glance." },
      { role: "Accounts team", benefit: "Employee grade and salary details flow directly into payroll no re-entry." },
      { role: "Management board", benefit: "Staff headcount and qualification reports for board meetings generated in seconds." },
    ],
    howItWorks: [
      { step: "01", title: "Create employee profile on joining", desc: "Fill the guided onboarding form. Documents uploaded digitally. Access credentials issued automatically." },
      { step: "02", title: "Profile updates throughout tenure", desc: "Promotions, leaves, appraisals, and salary revisions log automatically always current." },
      { step: "03", title: "Exit handled cleanly", desc: "Resignation logged, dues calculated, experience letter generated. Access revoked automatically." },
    ],
    mockupType: "staff-profile",
    relatedSlugs: ["payroll", "accounts-finance", "academic-calendar", "id-cards-certificates"],
  },

  {
    slug: "payroll",
    category: "Salary & Compensation",
    categorySlug: "payroll",
    title: "Payroll Management",
    heroImage: "/school/payroll.webp",
    iconName: "Banknote" ,
    tagline: "From attendance to payslip in one hour. Every month. Without errors.",
    heroStat: [
      { value: "< 1 hr", label: "Full payroll run" },
      { value: "Auto", label: "PF, ESI, TDS calc" },
      { value: "Digital", label: "Payslip delivery" },
    ],
    problemStatement: "Payroll that takes 3 days every month, manual PF and ESI calculations, and Excel sheets that break whenever a formula changes. There is a better way and your staff deserve it.",
    capabilities: [
      {
        icon: "DollarSign",
        title: "Salary Structure Setup",
        desc: "Define salary components basic, HRA, TA, special allowances, and deductions. Apply different structures to different staff grades.",
      },
      {
        icon: "Rocket",
        title: "Automated Salary Processing",
        desc: "LOP deductions and OT additions pull directly from attendance and leave data. Run the full payroll cycle in under one hour.",
      },
      {
        icon: "Smartphone",
        title: "Payslip Generation",
        desc: "Password-protected payslips generated and delivered to each employee's email and portal no printing, no manual distribution.",
      },
      {
        icon: "ShieldCheck",
        title: "Tax & PF Calculations",
        desc: "PF, ESI, PT, and TDS computed automatically per employee based on their profile and salary structure. Challans generated ready for payment.",
      },
      {
        icon: "FileText",
        title: "Payroll Reports",
        desc: "Monthly payroll summary, department-wise cost reports, arrear processing, and year-end Form 16 generation all from the same module.",
      },
    ],
    whoBenefits: [
      { role: "Accounts team", benefit: "Complete the full payroll cycle in under one hour including statutory calculations and payslip delivery." },
      { role: "HR administrator", benefit: "Process increments, arrears, and full-and-final settlements without spreadsheet gymnastics." },
      { role: "Teaching staff", benefit: "Receive their payslip digitally on time, every month with a full salary breakdown." },
      { role: "School management", benefit: "Accurate payroll cost reports without waiting for end-of-month summaries from accounts." },
    ],
    howItWorks: [
      { step: "01", title: "Attendance & leave data syncs in", desc: "At month-end, all attendance and approved leaves are already in the system payroll just picks them up." },
      { step: "02", title: "Run payroll with one click", desc: "Review auto-calculated figures, make any manual adjustments, and approve the payroll run." },
      { step: "03", title: "Payslips delivered, reports ready", desc: "Payslips go out to every employee. Statutory reports and challans are generated immediately after." },
    ],
    mockupType: "payroll",
    relatedSlugs: ["hrms", "accounts-finance", "student-information-system", "fee-finance"],
  },

  {
    slug: "id-cards-certificates",
    category: "Document & Credential Hub",
    categorySlug: "certificates",
    heroImage: "/school/Certification.webp",
    iconName: "FolderOpen",
    title: "Certificates & Document Management",
    tagline: "Generate 1,000 ID cards or certificates in minutes. Custom templates, print-ready.",
    heroStat: [
      { value: "Bulk", label: "Generation in minutes" },
      { value: "QR-verified", label: "Digital certificates" },
      { value: "Print-ready", label: "PDF output" },
    ],
    problemStatement: "Designing ID cards in Photoshop, typing certificates in Word, printing documents one by one schools spend days on documents that should take minutes.",
    capabilities: [
      {
        icon: "ScrollText",
        title: "Bonafide / Leaving Certificate Generation",
        desc: "Generate bonafide, character, and leaving certificates from templates in seconds with principal's digital signature, ready to issue on the spot.",
      },
      {
        icon: "PenTool",
        title: "Character Certificates",
        desc: "Maintain and issue character certificates from standardised templates. Add custom remarks and auto-populate student details from the SIS.",
      },
      {
        icon: "LayoutTemplate",
        title: "Custom Certificate Templates",
        desc: "Design your own ID card, marksheet, and certificate layouts using the drag-and-drop template builder matches your school's branding.",
      },
      {
        icon: "QrCode",
        title: "Digital Verification (QR-based)",
        desc: "Every certificate carries a unique QR code. Employers and institutions scan to verify authenticity instantly no phone calls to the school.",
      },
      {
        icon: "Printer",
        title: "Download & Print Options",
        desc: "Bulk PDF export for 1,000 ID cards or certificates in one file, or send individual documents directly to parent emails.",
      },
    ],
    whoBenefits: [
      { role: "Admin staff", benefit: "Generate 500 student ID cards in 10 minutes instead of two days." },
      { role: "Exam coordinator", benefit: "Marksheets populate from result data automatically no typing, no errors." },
      { role: "Front desk", benefit: "Issue bonafide and transfer certificates on the spot no wait for manual preparation." },
      { role: "Students & parents", benefit: "Receive certificates and marksheets digitally no need to visit school for routine documents." },
    ],
    howItWorks: [
      { step: "01", title: "Set up your templates once", desc: "Design ID card, certificate, and marksheet layouts using the builder. Upload your logo and signature." },
      { step: "02", title: "Select students and generate", desc: "Choose which students or staff need documents. Click generate system pulls all data automatically." },
      { step: "03", title: "Print or deliver digitally", desc: "Download print-ready PDFs or send individual documents directly to parent emails." },
    ],
    mockupType: "id-cards",
    relatedSlugs: ["student-information-system", "examination-results", "digital-admissions", "cumulative-records"],
  },

  {
    slug: "scholarship-management",
    category: "Financial Aid Hub",
    categorySlug: "scholarship",
    heroImage: "/school/scholarship.webp",
    iconName: "HeartHandshake",
    title: "Scholarship Management",
    tagline: "Track every scholarship application from eligibility to disbursement nothing falls through.",
    heroStat: [
      { value: "Auto", label: "Eligibility check" },
      { value: "Digital", label: "Approval workflow" },
      { value: "Full", label: "Disbursement trail" },
    ],
    problemStatement: "Scholarship processes are buried in paper forms, manual eligibility checks, and offline approvals. Students miss out and schools struggle with compliance. There's a better way.",
    capabilities: [
      {
        icon: "FilePenLine",
        title: "Scholarship Applications",
        desc: "Students and parents apply for scholarships digitally. Custom application forms per scholarship type with document upload.",
      },
      {
        icon: "CheckSquare",
        title: "Eligibility Tracking",
        desc: "Define eligibility criteria income, marks, category, etc. System auto-flags qualifying students and prevents ineligible applications.",
      },
      {
        icon: "Kanban",
        title: "Approval Workflow",
        desc: "Multi-level digital approval class teacher, coordinator, and principal review and approve in sequence. Full audit trail.",
      },
      {
        icon: "Coins",
        title: "Fund Disbursement Tracking",
        desc: "Log disbursement dates and amounts against each scholarship. Approved amounts automatically adjust fee dues in the finance module.",
      },
      {
        icon: "ChartColumn",
        title: "Reports & Government Integration",
        desc: "Generate government-format scholarship reports for NSP, state portals, and minority welfare submissions no manual data re-entry.",
      },
    ],
    whoBenefits: [
      { role: "Admissions coordinator", benefit: "Track all scholarship applications and approvals in one pipeline no spreadsheets." },
      { role: "Accounts team", benefit: "Approved scholarship amounts post automatically to the student's fee account." },
      { role: "School principal", benefit: "Approve applications digitally from anywhere with full context on eligibility and history." },
      { role: "Parents", benefit: "Apply online, track approval status, and see disbursements reflected in their fee account." },
    ],
    howItWorks: [
      { step: "01", title: "Student applies digitally", desc: "Fills the scholarship form, uploads documents. Eligibility is checked automatically." },
      { step: "02", title: "Multi-level approval", desc: "Application moves through the approval chain. Each approver notified in sequence." },
      { step: "03", title: "Disburse and reconcile", desc: "Approved amount posted to student fee account. Disbursement logged for government reporting." },
    ],
    mockupType: "scholarship",
    relatedSlugs: ["fee-finance", "student-information-system", "competitive-exams", "alumni-management"],
  },

  {
    slug: "front-office",
    category: "Reception Console",
    categorySlug: "frontoffice",
    title: "Front Office Management",
    heroImage: "/school/office.webp",
    iconName: "Monitor",
    tagline: "Manage every visitor, enquiry, and appointment from one professional front desk dashboard.",
    heroStat: [
      { value: "Digital", label: "Visitor log" },
      { value: "Trackable", label: "Every enquiry" },
      { value: "Auto", label: "Gate pass issuance" },
    ],
    problemStatement: "Front desk staff juggle paper visitor logs, enquiry notebooks, and manual gate passes while trying to handle a queue of parents and vendors. A digital front office changes everything.",
    capabilities: [
      {
        icon: "UserCheck",
        title: "Visitor Management System",
        desc: "Log every visitor parent, vendor, or guest with purpose, host, and entry/exit time. Photo capture option for enhanced security.",
      },
      {
        icon: "PhoneCall",
        title: "Inquiry Handling",
        desc: "Log walk-in and phone enquiries with caller details, interest level, and follow-up actions. All leads flow into the admissions pipeline.",
      },
      {
        icon: "CalendarCheck2",
        title: "Appointment Scheduling",
        desc: "Book and manage appointments for parents, vendors, and prospective families. Automated reminders sent to both parties.",
      },
      {
        icon: "Ticket",
        title: "Gate Pass Management",
        desc: "Issue digital gate passes for students leaving mid-day. Parent consent tracked. Security staff see active passes on their device.",
      },
      {
        icon: "History",
        title: "Communication Logs",
        desc: "Every front desk interaction call, visit, enquiry logged with timestamps and staff notes for full accountability.",
      },
    ],
    whoBenefits: [
      { role: "Front desk staff", benefit: "Handle visitor check-ins, enquiries, and gate passes from a single dashboard no paper books." },
      { role: "School principal", benefit: "Know who is in the school at any moment and review visitor logs for security and compliance." },
      { role: "Admissions team", benefit: "Every walk-in enquiry logged and flowing into the admissions pipeline nothing lost." },
      { role: "Parents", benefit: "Smooth, professional check-in experience and confirmation of their appointment details in advance." },
    ],
    howItWorks: [
      { step: "01", title: "Visitor checks in at the front desk", desc: "Staff log the visitor digitally purpose, host, and entry time. Visitor badge or pass generated instantly." },
      { step: "02", title: "Host notified and enquiry logged", desc: "The relevant staff member is notified. Enquiries are tagged and routed to the admissions pipeline." },
      { step: "03", title: "Exit recorded and log maintained", desc: "Exit time logged on departure. Full visitor history available for audit and security review." },
    ],
    mockupType: "front-office",
    relatedSlugs: ["digital-admissions", "helpdesk", "health-medical", "parent-portal"],
  },

  {
    slug: "health-medical",
    category: "Wellness & Care",
    categorySlug: "health",
    title: "Health & Medical Records",
    heroImage: "/school/doctor.webp",
     iconName: "Heart",
    tagline: "Every student's medical profile, health checkup, and incident record in one secure place.",
    heroStat: [
      { value: "Instant", label: "Medical alert access" },
      { value: "Full", label: "Checkup history" },
      { value: "Auto", label: "Vaccination reminders" },
    ],
    problemStatement: "Medical information lives in paper files that staff can't find in an emergency. Allergy alerts aren't visible to teachers. Vaccination records are impossible to compile for government audits.",
    capabilities: [
      {
        icon: "HeartPulse",
        title: "Student Medical Records",
        desc: "Store blood group, allergies, chronic conditions, medications, and disability status in every student's profile visible to authorised staff instantly.",
      },
      {
        icon: "Stethoscope",
        title: "Health Checkup Logs",
        desc: "Record annual health checkup results height, weight, vision, dental per student with trend tracking across years.",
      },
      {
        icon: "Syringe",
        title: "Vaccination Records",
        desc: "Maintain vaccination history per student. Generate class-wise vaccination status reports for government health programme compliance.",
      },
      {
        icon: "Phone",
        title: "Emergency Contacts",
        desc: "Emergency contacts stored against every student profile accessible to teachers, front office, and health staff with one tap.",
      },
      {
        icon: "AlertCircle",
        title: "Incident Reporting",
        desc: "Log health incidents injuries, illnesses, first-aid administered with timestamps, staff involved, and parent notification records.",
      },
      {
        icon: "Activity",
        title: "School Nurse & Health Management",
        desc: "Digitally manage student health records, nurse visits, treatments, and medication workflows with seamless coordination across staff, administration, and parents.",
      },
    ],
    whoBenefits: [
      { role: "School nurse / health in-charge", benefit: "Access full student medical history and log checkup results from any device no paper files." },
      { role: "Class teachers", benefit: "See allergy and medical alerts for students in their class before a field trip or activity." },
      { role: "School admin", benefit: "Generate vaccination and health checkup compliance reports for government health audits in minutes." },
      { role: "Parents", benefit: "Know that staff have accurate medical information about their child and receive instant notification of any incident." },
    ],
    howItWorks: [
      { step: "01", title: "Medical profile set at admission", desc: "Parents fill health details during admission. Allergy and condition alerts appear on the student profile immediately." },
      { step: "02", title: "Checkups and incidents logged throughout the year", desc: "Health staff log checkup results and incidents digitally. Parents notified of any incident automatically." },
      { step: "03", title: "Reports generated for compliance", desc: "Vaccination and health summary reports for government audits generated in one click." },
    ],
    mockupType: "health",
    relatedSlugs: ["student-information-system", "front-office", "parent-portal", "sports-management"],
  },

  {
    slug: "co-curricular",
    category: "Beyond Academics",
    categorySlug: "cocurricular",
    heroImage: "/school/activity.webp",
    iconName: "Star",
    title: "Co-Curricular Activities Management",
    tagline: "Track every club, competition, and talent across every student not just the academics.",
    heroStat: [
      { value: "All clubs", label: "Managed centrally" },
      { value: "Per student", label: "Activity portfolio" },
      { value: "Competition", label: "Records tracked" },
    ],
    problemStatement: "Co-curricular achievements are scattered across teacher notebooks, WhatsApp messages, and prize certificates. Building a complete student profile means pulling from a dozen places.",
    capabilities: [
      {
        icon: "Music",
        title: "Club Management",
        desc: "Create and manage school clubs music, dance, drama, robotics, debate, and more. Assign teacher-in-charges, track membership, and schedule sessions.",
      },
      {
        icon: "CalendarCheck",
        title: "Event Participation Tracking",
        desc: "Log every student's participation in school events inter-house competitions, cultural events, and external fests against their profile.",
      },
      {
        icon: "Trophy",
        title: "Competition Records",
        desc: "Record competition results, rankings, and awards at school, district, state, and national levels per student.",
      },
      {
        icon: "Sparkles",
        title: "Student Talent Profiles",
        desc: "Build a co-curricular portfolio for each student useful for cumulative records, college applications, and transfer certificates.",
      },
      {
        icon: "ChartNoAxesCombined",
        title: "NCC & Co-Curricular Reports",
        desc: "Analyze participation and performance across NCC, Scouts & Guides, clubs, and school programs with centralized reporting and actionable insights.",
      },
      {
        icon: "CalendarRange",
        title: "Academic Activities & Event Management",
        desc: "Plan, schedule, and manage monthly academic activities, annual functions, school events, competitions, and special programs with centralized coordination and reporting.",
      },
    ],
    whoBenefits: [
      { role: "Activity coordinators", benefit: "Manage club rosters, sessions, and competition records from one dashboard no paper registers." },
      { role: "Class teachers", benefit: "See each student's co-curricular profile alongside academics for a complete picture." },
      { role: "Students", benefit: "Build a documented portfolio of achievements useful for college applications and scholarships." },
      { role: "School management", benefit: "Showcase school-wide co-curricular achievements with accurate data for marketing and accreditation." },
    ],
    howItWorks: [
      { step: "01", title: "Set up clubs and activities", desc: "Create clubs, assign teacher-in-charges, and enrol students. Done once at the start of each term." },
      { step: "02", title: "Log participation and results", desc: "Teachers log session attendance, competition results, and awards directly from their dashboard." },
      { step: "03", title: "Profile and reports built automatically", desc: "Every log entry builds the student's co-curricular portfolio and feeds into activity reports." },
    ],
    mockupType: "co-curricular",
    relatedSlugs: ["student-information-system", "sports-management", "academic-calendar", "competitive-exams"],
  },

  {
    slug: "inventory",
    category: "Store & Supplies",
    categorySlug: "inventory",
    heroImage: "/school/inventory.webp",
    iconName: "Package",
    title: "Inventory & Facilities Management",
    tagline: "Library, labs, uniforms, and every asset tracked, managed, and never running out.",
    heroStat: [
      { value: "Real-time", label: "Stock levels" },
      { value: "Auto", label: "Reorder alerts" },
      { value: "Full", label: "Asset history" },
    ],
    problemStatement: "Lab equipment disappears, stationery stock runs out mid-term, and library books go missing for years. Inventory management shouldn't require a quarterly physical audit.",
    capabilities: [
      {
        icon: "BookOpen",
        title: "Library Management",
        desc: "Catalogue books with ISBN, author, and subject. Manage issue and return with due date alerts and fine calculation for late returns.",
      },
      {
        icon: "Shirt",
        title: "Uniform Inventory",
        desc: "Track uniform stock by size and type. Log sales to students, manage low-stock alerts, and reconcile with the finance module.",
      },
      {
        icon: "FlaskConical",
        title: "Lab Equipment Tracking",
        desc: "Catalogue lab equipment with location, condition, and maintenance schedule. Log issue and return per class and teacher.",
      },
      {
        icon: "Bell",
        title: "Stock Alerts & Reordering",
        desc: "Set reorder levels for consumables stationery, lab chemicals, cleaning supplies. Automatic alerts when stock hits the threshold.",
      },
      {
        icon: "Briefcase",
        title: "Vendor Management",
        desc: "Maintain a vendor directory with payment terms. Track invoices, payment status, and outstanding amounts per vendor.",
      },
      {
        icon: "Archive",
        title: "Curriculum & Publications Management",
        desc: "Organize and manage school magazines, curriculum books, and academic documents with efficient tracking and easy retrieval.",
      },
    ],
    whoBenefits: [
      { role: "Librarian", benefit: "Issue and return books digitally overdue alerts go to students and parents automatically." },
      { role: "Lab / store in-charge", benefit: "Real-time stock levels without physical counting know what's available before a class starts." },
      { role: "Accounts team", benefit: "Asset values and depreciation flow into financial reports no separate asset register to maintain." },
      { role: "Management board", benefit: "Full asset register with current values for insurance and compliance always up to date." },
    ],
    howItWorks: [
      { step: "01", title: "Catalogue assets and set stock levels", desc: "Enter your inventory once assets and consumables. Set quantities and reorder thresholds." },
      { step: "02", title: "Track issues, returns, and purchases", desc: "All movements logged in real time. Purchase requests go through the approval workflow." },
      { step: "03", title: "Alerts keep you ahead", desc: "Low stock and maintenance alerts mean you're never caught unprepared." },
    ],
    mockupType: "inventory",
    relatedSlugs: ["accounts-finance", "asset-management", "fee-finance", "lms"],
  },

  {
    slug: "parent-portal",
    category: "Family Connect",
    categorySlug: "parentportal",
    heroImage: "/school/partent.webp",
     iconName: "MessageCircleHeart" ,
    title: "Parent Portal & PTM Management",
    tagline: "Parents stay informed and engaged fees, attendance, results, and teachers all in one app.",
    heroStat: [
      { value: "30s", label: "Average payment time" },
      { value: "No app", label: "Download needed" },
      { value: "Instant", label: "PTM scheduling" },
    ],
    problemStatement: "Parents are the most important stakeholders in a child's education yet most schools give them the least visibility. A proper parent portal changes the relationship entirely.",
    capabilities: [
      {
        icon: "LayoutDashboard",
        title: "Parent Login Dashboard",
        desc: "A personalised dashboard showing attendance, fee dues, recent results, upcoming events, and notices everything in one place.",
      },
      {
        icon: "LineChart",
        title: "Student Progress Tracking",
        desc: "View subject-wise marks, assignment scores, attendance percentage, and co-curricular records updated in real time.",
      },
      {
        icon: "CalendarDays",
        title: "PTM Scheduling & Feedback",
        desc: "Book parent-teacher meeting slots online. Post-PTM, teachers log feedback and action points against each student profile.",
      },
      {
        icon: "MessageCircle",
        title: "Communication (Messages & Alerts)",
        desc: "Direct messaging with teachers, class-wise announcements, and instant SMS/app alerts for attendance, fees, and notices.",
      },
      {
        icon: "Receipt",
        title: "Fee & Attendance View",
        desc: "Parents see outstanding dues, payment history, and downloadable receipts plus daily attendance with absent-day notifications.",
      },
      {
        icon: "Users",
        title: "Multi-child Support",
        desc: "Parents with more than one child see all accounts from a single login no separate credentials per child.",
      },
    ],
    whoBenefits: [
      { role: "Parents", benefit: "Pay fees, view results, track attendance, and message teachers from their phone any time, anywhere." },
      { role: "Class teachers", benefit: "Communicate with parents without sharing personal phone numbers or managing WhatsApp groups." },
      { role: "Accounts team", benefit: "Fewer walk-ins, fewer phone calls, and fully automated receipt generation." },
      { role: "School management", benefit: "Higher parent engagement and higher fee collection rates because convenience reduces delays." },
    ],
    howItWorks: [
      { step: "01", title: "Parent receives login credentials", desc: "Credentials generated automatically when the student is admitted and sent via SMS." },
      { step: "02", title: "Logs in and sees full dashboard", desc: "Attendance, fees, results, and notices shown on first screen nothing hidden." },
      { step: "03", title: "Stays engaged throughout the year", desc: "Automatic alerts for absences, dues, results, and PTM bookings keep parents involved." },
    ],
    mockupType: "parent-portal",
    relatedSlugs: ["fee-finance", "student-information-system", "transport", "helpdesk"],
  },

  {
    slug: "helpdesk",
    category: "Support Desk",
    categorySlug: "helpdesk",
    title: "Helpdesk & Support System",
    heroImage: "/school/helpdesk.webp",
     iconName: "LifeBuoy",
    tagline: "Every complaint tracked, assigned, and resolved with full visibility for staff and parents.",
    heroStat: [
      { value: "Auto", label: "Ticket assignment" },
      { value: "SLA", label: "Resolution tracking" },
      { value: "Zero", label: "Lost complaints" },
    ],
    problemStatement: "Parent complaints arrive by call, email, and walk-in. They get logged on sticky notes or not at all. Nobody knows what's pending and what's resolved. A proper helpdesk fixes this.",
    capabilities: [
      {
        icon: "Ticket",
        title: "Ticket Raising System",
        desc: "Staff and parents raise support tickets digitally for fee queries, academic concerns, transport issues, or facility complaints.",
      },
      {
        icon: "LayoutDashboard",
        title: "Complaint Tracking",
        desc: "Every ticket assigned to a responsible staff member. Status visible to the raiser open, in-progress, resolved.",
      },
      {
        icon: "CheckSquare",
        title: "Issue Resolution Workflow",
        desc: "Tickets escalate automatically if unresolved within defined timeframes. Multi-level escalation paths configured per issue type.",
      },
      {
        icon: "HelpCircle",
        title: "Query & Doubt Management",
        desc: "Manage parent and student queries with organized ticketing, status tracking, and efficient resolution workflows across departments.",
      },
      {
        icon: "Clock",
        title: "Priority & SLA Tracking",
        desc: "Set priority levels and SLA timelines per issue category. Dashboard shows overdue tickets requiring immediate attention.",
      },
      {
        icon: "BookOpen",
        title: "FAQ & Knowledge Base",
        desc: "Publish answers to common questions fee structure, admission process, transport routes. Reduces repetitive queries to staff.",
      },
    ],
    whoBenefits: [
      { role: "Front desk staff", benefit: "Log every query or complaint as a ticket nothing handled informally and nothing forgotten." },
      { role: "School management", benefit: "See all open issues, SLA compliance rates, and which departments have the most unresolved complaints." },
      { role: "Parents", benefit: "Raise a concern and track its progress without calling the school repeatedly." },
      { role: "Department heads", benefit: "See all tickets assigned to their team and resolve within SLA to avoid escalation." },
    ],
    howItWorks: [
      { step: "01", title: "Ticket raised digitally", desc: "Parent or staff logs the issue. System assigns to the correct team and sends acknowledgement." },
      { step: "02", title: "Assigned staff resolves", desc: "Responsible person works the ticket, updates status, and adds resolution notes." },
      { step: "03", title: "Closed and rated", desc: "Ticket closes with resolution logged. Raiser can rate satisfaction. Overdue issues auto-escalate." },
    ],
    mockupType: "helpdesk",
    relatedSlugs: ["front-office", "parent-portal", "transport", "fee-finance"],
  },

  {
    slug: "sports-management",
    category: "Athletics Hub",
    categorySlug: "sports",
    heroImage: "/school/sportsmanagement.webp",
    title: "Sports Management",
     iconName: "Trophy" ,
    tagline: "Manage teams, fixtures, fitness records, and tournaments all in one sports hub.",
    heroStat: [
      { value: "All teams", label: "Managed centrally" },
      { value: "Fixture", label: "Scheduling & results" },
      { value: "Per athlete", label: "Fitness tracking" },
    ],
    problemStatement: "Sports records live in coaches' personal notebooks. Fixtures are announced last minute. Fitness data is never tracked. Schools investing in sports deserve a proper management system.",
    capabilities: [
      {
        icon: "Users",
        title: "Sports Team Management",
        desc: "Create and manage teams per sport cricket, football, basketball, athletics, and more. Assign coaches and track squad rosters.",
      },
      {
        icon: "CalendarDays",
        title: "Match Scheduling",
        desc: "Schedule inter-house, inter-school, and tournament fixtures with venue, date, and time. Notify participants automatically.",
      },
      {
        icon: "ChartColumn",
        title: "Performance Tracking",
        desc: "Log match results, individual performance stats, and player ratings. Track improvement over the season.",
      },
      {
        icon: "Award",
        title: "Sports Achievements & Levels",
        desc: "Track student participation across school, district, state, national, and international sports events with selection status, achievements, and performance records.",
      },
      {
        icon: "Activity",
        title: "Fitness Records",
        desc: "Record fitness assessments speed, strength, endurance per student-athlete. Track trends across the academic year.",
      },
      {
        icon: "Trophy",
        title: "Tournament Management",
        desc: "Organise full tournaments with brackets, round-robin tables, and result tracking. Publish standings for students and parents.",
      },
    ],
    whoBenefits: [
      { role: "Sports coaches", benefit: "Manage rosters, log match results, and track player fitness from their phone no paper registers." },
      { role: "Sports coordinator", benefit: "Schedule all fixtures and tournaments in one place with automated notifications to participants." },
      { role: "Students", benefit: "See their team fixtures, individual performance stats, and fitness progress over time." },
      { role: "School management", benefit: "Showcase sports achievements with accurate records for accreditation and marketing." },
    ],
    howItWorks: [
      { step: "01", title: "Set up teams and squads", desc: "Create teams per sport, assign coaches, and enrol student-athletes at the start of the year." },
      { step: "02", title: "Schedule fixtures and log results", desc: "Publish the fixture list. Coaches log match results and player stats after each game." },
      { step: "03", title: "Track performance and report", desc: "Individual and team performance data builds into reports and student co-curricular profiles." },
    ],
    mockupType: "sports",
    relatedSlugs: ["co-curricular", "student-information-system", "academic-calendar", "health-medical"],
  },

  {
    slug: "asset-management",
    category: "Resource Tracker",
    categorySlug: "assets",
    heroImage: "/school/assets.webp",
    title: "Asset Management",
    iconName: "Container" ,
    tagline: "Know what you own, where it is, and when to service it without a physical audit.",
    heroStat: [
      { value: "Full", label: "Asset register" },
      { value: "Auto", label: "Maintenance alerts" },
      { value: "Live", label: "Depreciation tracking" },
    ],
    problemStatement: "Schools own thousands of assets computers, furniture, lab equipment, projectors with no single record of where they are, what they're worth, or when they need servicing.",
    capabilities: [
      {
        icon: "Boxes",
        title: "Asset Register",
        desc: "Catalogue every physical asset computers, furniture, projectors, sports equipment with purchase date, value, location, and condition.",
      },
      {
        icon: "MapPin",
        title: "Allocation Tracking",
        desc: "Assign assets to departments, rooms, or individual staff. Transfer assets between locations with a full movement history.",
      },
      {
        icon: "Wrench",
        title: "Maintenance Logs",
        desc: "Log service and repair history per asset. Schedule preventive maintenance and receive alerts before AMC and warranty expiry.",
      },
      {
        icon: "TrendingDown",
        title: "Depreciation Tracking",
        desc: "Track asset value over time with configurable depreciation schedules useful for balance sheet reporting and insurance.",
      },
      {
        icon: "FileText",
        title: "Asset Lifecycle Reports",
        desc: "Generate full asset lifecycle reports from purchase to disposal. Supports insurance renewals, audits, and board reporting.",
      },
    ],
    whoBenefits: [
      { role: "Facilities manager", benefit: "Know the location and condition of every asset no physical audit needed to answer the question." },
      { role: "Accounts team", benefit: "Asset values and depreciation flow directly into financial reports no separate spreadsheet." },
      { role: "IT coordinator", benefit: "Track every computer, printer, and device with service history and warranty expiry alerts." },
      { role: "Management board", benefit: "Full asset register with current values for insurance, compliance, and capital planning." },
    ],
    howItWorks: [
      { step: "01", title: "Catalogue all assets once", desc: "Enter existing assets with purchase value, location, and condition. Import from Excel for large registers." },
      { step: "02", title: "Track movements and maintenance", desc: "Log transfers, service events, and repairs as they happen. Alerts fire for scheduled maintenance." },
      { step: "03", title: "Reports for audits and planning", desc: "Asset lifecycle and depreciation reports available on demand no data preparation required." },
    ],
    mockupType: "inventory",
    relatedSlugs: ["inventory", "accounts-finance", "transport", "helpdesk"],
  },

  {
    slug: "accounts-finance",
    category: "Ledger & Books",
    categorySlug: "accounts",
    heroImage: "/school/Accountant.webp",
    iconName: "BookOpen",
    title: "Accounts & Finance",
    tagline: "Double-entry bookkeeping tailored for schools. P&L and balance sheet on demand.",
    heroStat: [
      { value: "Double-entry", label: "Bookkeeping standard" },
      { value: "Auto", label: "Bank reconciliation" },
      { value: "1-click", label: "P&L and balance sheet" },
    ],
    problemStatement: "Most school accounting systems are either too complex or too simple. Schools need proper accounts with context that understands fee heads, grant income, budget tracking, and multi-campus structures.",
    capabilities: [
      {
        icon: "ArrowUpDown",
        title: "Income & Expense Tracking",
        desc: "Every fee payment, salary run, vendor invoice, and miscellaneous income or expense tracked with double-entry precision.",
      },
      {
        icon: "PieChart",
        title: "Budget Planning",
        desc: "Allocate annual budgets to departments. Track actuals vs budget in real time with overspend alerts before the cheque is written.",
      },
      {
        icon: "ChartColumn",
        title: "Financial Statements",
        desc: "Generate income & expenditure statements and balance sheets on demand formatted for management review or board presentation.",
      },
      {
        icon: "Search",
        title: "Audit Reports",
        desc: "Full drill-down audit trail from every ledger entry back to the source document fee payment, salary run, purchase invoice, or manual entry.",
      },
      {
        icon: "Building2",
        title: "Bank Reconciliation",
        desc: "Import bank statements and auto-match transactions. Flag unmatched entries and reconcile in minutes, not days.",
      },
      {
        icon: "Layers",
        title: "Multi-campus Support",
        desc: "Manage finances for multiple campuses from a single login. Consolidated reports or branch-wise breakdowns your choice.",
      },
    ],
    whoBenefits: [
      { role: "Accounts staff", benefit: "Process transactions faster with school-specific templates and automatic fee-to-ledger posting." },
      { role: "CA / Auditor", benefit: "Full audit trail from every ledger entry back to the source document audit prep in hours, not weeks." },
      { role: "School principal / director", benefit: "On-demand P&L without waiting for end-of-month reports from the accounts team." },
      { role: "Trust / management board", benefit: "Consolidated financial reports across all campuses with one login." },
    ],
    howItWorks: [
      { step: "01", title: "Fee payments post automatically", desc: "Every fee collected flows directly into the ledger no manual journal entries for routine income." },
      { step: "02", title: "Enter other income and expenses", desc: "Log vendor invoices, payroll, and miscellaneous expenses. GST codes auto-populate from your configuration." },
      { step: "03", title: "Reconcile and report", desc: "Import bank statement, auto-match, and generate final reports. Month-end done in one afternoon." },
    ],
    mockupType: "accounts",
    relatedSlugs: ["fee-finance", "payroll", "asset-management", "inventory"],
  },

  {
    slug: "lms",
    category: "Digital Classroom",
    categorySlug: "lms",
    heroImage: "/school/lms.webp",
    title: "Learning Management System",
    iconName: "MonitorSmartphone",
    tagline: "A full LMS built into your school ERP no separate subscription needed.",
    heroStat: [
      { value: "Video + PDF", label: "Content formats" },
      { value: "Per-student", label: "Progress tracking" },
      { value: "Built-in", label: "No third-party LMS" },
    ],
    problemStatement: "Schools pay for a separate LMS, then struggle to keep it in sync with actual school data. The built-in learning studio lives inside the same platform student data, results, and content all connected.",
    capabilities: [
      {
        icon: "Video",
        title: "Course Content Upload",
        desc: "Upload videos, PDFs, presentations, and links. Organise by subject, chapter, and class accessible from any device.",
      },
      {
        icon: "FilePenLine",
        title: "Assignment Management",
        desc: "Teachers create assignments with instructions, due dates, and marks weightage. Students submit files or typed answers from any device.",
      },
      {
        icon: "FlaskConical",
        title: "Online Tests & Quizzes",
        desc: "Build and schedule online tests linked to lesson content. MCQ exams auto-grade instantly; results visible to students immediately.",
      },
      {
        icon: "Eye",
        title: "Student Progress Tracking",
        desc: "See exactly which students watched which lessons, submitted which assignments, and scored how on each quiz per student, per subject.",
      },
      {
        icon: "MessageCircle",
        title: "Teacher-Student Interaction",
        desc: "Teachers add inline feedback on submitted assignments. Discussion threads per lesson allow students to ask questions asynchronously.",
      },
      {
        icon: "Award",
        title: "Completion Certificates",
        desc: "Auto-generate and email course completion certificates to students who finish a full subject module or elective course.",
      },
    ],
    whoBenefits: [
      { role: "Teachers", benefit: "Record once, teach many times. Flip the classroom or extend learning beyond school hours without extra work." },
      { role: "Students", benefit: "Review lessons at their own pace, re-watch complex topics, and complete linked assessments from home." },
      { role: "School management", benefit: "Reduce dependency on external tuition by offering structured self-study content within the school ecosystem." },
      { role: "Parents", benefit: "Know which lessons their child has accessed and whether linked assessments have been completed." },
    ],
    howItWorks: [
      { step: "01", title: "Upload lesson content", desc: "Teacher uploads video or PDF, adds a title and chapter tag, and sets the release date." },
      { step: "02", title: "Students access and engage", desc: "Students see available lessons in their portal. Progress tracked automatically as they engage." },
      { step: "03", title: "Completion data feeds into reports", desc: "Engagement data appears in teacher dashboards and feeds into academic performance records." },
    ],
    mockupType: "lms",
    relatedSlugs: ["examination-results", "student-information-system", "parent-portal", "academic-calendar"],
  },

  {
    slug: "transport",
    category: "Fleet & Routes",
    categorySlug: "transport",
    heroImage: "/school/transport.webp",
    title: "Transport Management",
    iconName: "Bus",
    tagline: "Routes, stops, GPS tracking, and parent alerts all in one transport dashboard.",
    heroStat: [
      { value: "GPS", label: "Live vehicle tracking" },
      { value: "Auto", label: "Parent arrival alerts" },
      { value: "Route-wise", label: "Fee collection" },
    ],
    problemStatement: "Transport is one of the highest-risk operations in any school and most schools manage it with a notebook and personal phone calls. Parents deserve better visibility and schools need better control.",
    capabilities: [
      {
        icon: "Map",
        title: "Bus Route Planning",
        desc: "Define bus routes, stops, pickup times, and assigned vehicles. Adjust routes mid-year as student enrolment changes.",
      },
      {
        icon: "UserCheck",
        title: "Student Transport Allocation",
        desc: "Assign each student to their pickup and drop stop. Bulk import from Excel for large transport rosters.",
      },
      {
        icon: "MapPin",
        title: "GPS Tracking Integration",
        desc: "Track bus locations live from the admin dashboard. Parents see their bus on a map in the parent app.",
      },
      {
        icon: "Truck",
        title: "Driver & Vehicle Records",
        desc: "Maintain driver licences, vehicle RC, insurance, and PUC details with expiry alerts stay compliant automatically.",
      },
      {
        icon: "Coins",
        title: "Transport Fee Management",
        desc: "Collect transport fees as a separate fee head route-wise pricing, term billing, and reconciliation all handled.",
      },
    ],
    whoBenefits: [
      { role: "Transport coordinator", benefit: "Manage all routes, vehicles, and drivers from one dashboard no more spreadsheet juggling." },
      { role: "Parents", benefit: "Know exactly where the bus is and when it will arrive complete peace of mind." },
      { role: "School management", benefit: "Transport compliance documents and vehicle records with automatic expiry reminders." },
      { role: "Accounts team", benefit: "Transport fee collection and reconciliation handled in the same fee module no separate system." },
    ],
    howItWorks: [
      { step: "01", title: "Configure routes and assign students", desc: "Set up routes and stops, then assign students done once at the start of the year." },
      { step: "02", title: "GPS devices sync live", desc: "GPS units in buses stream location data to the platform. Parents and admin see real-time positions." },
      { step: "03", title: "Alerts and fees automated", desc: "Parent arrival alerts fire automatically. Transport fees generate and reconcile alongside tuition fees." },
    ],
    mockupType: "transport",
    relatedSlugs: ["student-information-system", "fee-finance", "parent-portal", "asset-management"],
  },

  {
    slug: "cumulative-records",
    category: "Lifelong Archive",
    categorySlug: "cumulative",
    heroImage: "/school/classroom.webp",
    iconName: "Archive",
    title: "Cumulative Academic Records",
    tagline: "Every year, every result, every milestone one complete academic history per student.",
    heroStat: [
      { value: "Year-wise", label: "Academic history" },
      { value: "Trend", label: "Performance analytics" },
      { value: "TC-ready", label: "Transfer data export" },
    ],
    problemStatement: "When a student leaves the school or applies for a scholarship, assembling their complete academic history takes days. It should be a one-click export.",
    capabilities: [
      {
        icon: "History",
        title: "Year-wise Academic History",
        desc: "Every result, attendance record, behaviour note, and co-curricular achievement organised by academic year, going back to admission.",
      },
      {
        icon: "TrendingUp",
        title: "Performance Trends",
        desc: "Visualise a student's academic trajectory subject-wise improvement, attendance patterns, and rank changes across years.",
      },
      {
        icon: "FileDown",
        title: "Transfer Certificate Data",
        desc: "All data required for TC issuance last class, conduct grade, fee clearance status pre-populated and ready to export.",
      },
      {
        icon: "ChartColumn",
        title: "Long-term Student Analytics",
        desc: "School-wide analytics on cohort performance over multiple years useful for NAAC, board accreditation, and strategic planning.",
      },
      {
        icon: "Link2",
        title: "Cross-module Data Aggregation",
        desc: "Pulls from attendance, examination, fee, health, and co-curricular modules to build a truly comprehensive student record.",
      },
      {
        icon: "ChartNoAxesCombined",
        title: "Dynamic Insights & Analytics",
        desc: "Visualize real-time school data through interactive charts, trends, and performance metrics for smarter, data-driven decision making.",
      },
    ],
    whoBenefits: [
      { role: "School principal", benefit: "Review any student's complete history in seconds for recommendations, transfers, or parent meetings." },
      { role: "Exam coordinator", benefit: "Year-on-year performance comparison for academic planning and result analysis." },
      { role: "Students & parents", benefit: "A complete, verifiable academic record available for college applications and scholarship submissions." },
      { role: "Management board", benefit: "Multi-year cohort performance data for NAAC and board accreditation submissions." },
    ],
    howItWorks: [
      { step: "01", title: "Data accumulates automatically", desc: "Every module attendance, exams, fees, health contributes to the cumulative record as events happen." },
      { step: "02", title: "View the complete timeline", desc: "Open any student profile and scroll through their entire academic life at the school year by year." },
      { step: "03", title: "Export for TC or reporting", desc: "One-click TC data export or download the full academic record as a PDF for external submission." },
    ],
    mockupType: "student-profile",
    relatedSlugs: ["student-information-system", "examination-results", "id-cards-certificates", "alumni-management"],
  },

  {
    slug: "alumni-management",
    category: "Graduate Network",
    categorySlug: "alumni",
    heroImage: "/school/alumini.webp",
    iconName: "Network" ,
    title: "Alumni Management",
    tagline: "Keep your alumni connected, engaged, and contributing long after graduation.",
    heroStat: [
      { value: "Searchable", label: "Alumni database" },
      { value: "Events", label: "& meetup management" },
      { value: "Donation", label: "Tracking built-in" },
    ],
    problemStatement: "Schools lose touch with their alumni the moment they graduate. An engaged alumni network is a school's greatest long-term asset it just needs the right tools to maintain it.",
    capabilities: [
      {
        icon: "Database",
        title: "Alumni Database",
        desc: "Auto-populated from the student SIS at graduation. Alumni update their profiles with current occupation, location, and contact details.",
      },
      {
        icon: "Users",
        title: "Networking Platform",
        desc: "Alumni connect with each other by batch year, location, or industry. Directory searchable by school staff and verified alumni.",
      },
      {
        icon: "CalendarDays",
        title: "Events & Meetups",
        desc: "Organise and promote alumni reunions, guest talks, and mentorship events. Online registration and attendance tracking built in.",
      },
      {
        icon: "Heart",
        title: "Donations & Contributions",
        desc: "Accept and track alumni donations for scholarships, infrastructure, or school development funds. Tax receipts generated automatically.",
      },
      {
        icon: "Briefcase",
        title: "Placement Support",
        desc: "Alumni post job opportunities for current students and recent graduates. Placement tracking and success stories documented.",
      },
    ],
    whoBenefits: [
      { role: "School management", benefit: "Build a financially contributing alumni network that supports scholarships and school development." },
      { role: "Alumni coordinator", benefit: "Manage the entire alumni relationship database, events, communications from one dashboard." },
      { role: "Current students", benefit: "Access alumni for mentorship, career guidance, and placement opportunities through a verified network." },
      { role: "Alumni", benefit: "Stay connected to their school community and give back in ways that matter financially or as mentors." },
    ],
    howItWorks: [
      { step: "01", title: "Students graduate into the alumni database", desc: "Graduating students automatically move into the alumni module. Login credentials sent for the alumni portal." },
      { step: "02", title: "Alumni stay engaged", desc: "Event invites, newsletters, and networking features keep alumni connected over the years." },
      { step: "03", title: "Contributions and placements tracked", desc: "Donations logged and receipted. Placement opportunities shared and tracked in the system." },
    ],
    mockupType: "alumni",
    relatedSlugs: ["student-information-system", "cumulative-records", "scholarship-management", "academic-calendar"],
  },

  {
    slug: "competitive-exams",
    category: "Talent Assessment",
    categorySlug: "competitive",
    title: "Competitive Exams",
    iconName: "Medal" ,
    heroImage: "/school/examresult.webp",
    tagline: "Prepare, register, and track student performance across scholarships, Olympiads, and government exams.",
    heroStat: [
      { value: "All levels", label: "School to national" },
      { value: "Centrally", label: "Managed registrations" },
      { value: "Per student", label: "Result tracking" },
    ],
    problemStatement: "Scholarship exams, Olympiads, and government competitive tests involve separate registrations, result tracking, and preparation resources scattered across teachers and email threads. There's a better way.",
    capabilities: [
      {
        icon: "Award",
        title: "Scholarship Exams",
        desc: "Manage registrations and results for national and state scholarship examinations NMMS, NSP-linked tests, and state merit scholarships.",
      },
      {
        icon: "Star",
        title: "Olympiads",
        desc: "Register students for IMO, NSO, IEO, and other Olympiad examinations. Track participation and results at school, zonal, and national levels.",
      },
      {
        icon: "Globe",
        title: "State / National / International Quizzes",
        desc: "Coordinate participation in quizzes and knowledge competitions across all levels. Log results and awards against student profiles.",
      },
      {
        icon: "ScrollText",
        title: "Government Exams",
        desc: "Track student registrations and results for government-conducted competitive exams relevant to school-level students.",
      },
      {
        icon: "ChartColumn",
        title: "Performance Analytics",
        desc: "School-wide analytics on competitive exam participation rates, subject-wise performance, and top performers useful for academic planning and recognition.",
      },
      {
        icon: "Search",
        title: "Talent Search Examinations",
        desc: "Manage participation and results for national and state-level talent search exams like NTSE and other aptitude-based competitive assessments conducted for school students.",
      },
    ],
    whoBenefits: [
      { role: "Academic coordinator", benefit: "Manage all competitive exam registrations and results from one dashboard no spreadsheets, no chasing teachers." },
      { role: "Subject teachers", benefit: "Know which students are registered for which exams and track how their subject performs at competitive level." },
      { role: "Students", benefit: "Clear visibility of upcoming exams, registration status, and past results in their profile." },
      { role: "School management", benefit: "Competitive exam performance data for marketing, accreditation, and parent communication." },
    ],
    howItWorks: [
      { step: "01", title: "Register students for exams", desc: "Coordinators log registrations per exam with student lists, fees paid, and exam dates." },
      { step: "02", title: "Enter results after exams", desc: "Scores and ranks entered manually or imported. Results attach to student profiles automatically." },
      { step: "03", title: "Analyse and recognise", desc: "School-wide analytics identify top performers. Achievements documented in student co-curricular portfolios." },
    ],
    mockupType: "competitive",
    relatedSlugs: ["examination-results", "scholarship-management", "co-curricular", "lms"],
  },
];

export function getFeature(slug) {
  return FEATURES.find((f) => f.slug === slug) ?? null;
}

export function getRelatedFeatures(feature) {
  return feature.relatedSlugs
    .map((slug) => FEATURES.find((f) => f.slug === slug))
    .filter(Boolean)
    .slice(0, 4);
}

export function getFeaturesByCategory(categorySlug) {
  return FEATURES.filter((f) => f.categorySlug === categorySlug);
}

export const CATEGORIES = [
  { slug: "admissions", label: "Smart Enrollment Hub", icon: "ClipboardList" },
  { slug: "sis", label: "Central Student Hub", icon: "Users" },
  { slug: "academic", label: "Schedule & Planner", icon: "CalendarDays" },
  { slug: "finance", label: "Revenue & Collections", icon: "CircleDollarSign" },
  { slug: "examination", label: "Assessment & Grading", icon: "FileCheck" },
  { slug: "hr", label: "Staff & Workforce", icon: "UserCog" },
  { slug: "payroll", label: "Salary & Compensation", icon: "Banknote" },
  { slug: "certificates", label: "Document & Credential Hub", icon: "ScrollText" },
  { slug: "scholarship", label: "Financial Aid Hub", icon: "Award" },
  { slug: "frontoffice", label: "Reception Console", icon: "ConciergeBell" },
  { slug: "health", label: "Wellness & Care", icon: "HeartPulse" },
  { slug: "cocurricular", label: "Beyond Academics", icon: "Music" },
  { slug: "inventory", label: "Store & Supplies", icon: "Package" },
  { slug: "parentportal", label: "Family Connect", icon: "Smartphone" },
  { slug: "helpdesk", label: "Support Desk", icon: "LifeBuoy" },
  { slug: "sports", label: "Athletics Hub", icon: "Trophy" },
  { slug: "assets", label: "Resource Tracker", icon: "Boxes" },
  { slug: "accounts", label: "Ledger & Books", icon: "BookMarked" },
  { slug: "lms", label: "Digital Classroom", icon: "GraduationCap" },
  { slug: "transport", label: "Fleet & Routes", icon: "Bus" },
  { slug: "cumulative", label: "Lifelong Archive", icon: "History" },
  { slug: "alumni", label: "Graduate Network", icon: "Network" },
  { slug: "competitive", label: "Talent Assessment", icon: "Star" },
];